/**
 * Iter-234h — Shared visual Dress Code + Grooming picker.
 *
 * Single source of truth for the dress code module used by:
 *   - Client / Admin Create Gig
 *   - Admin Edit Gig (AdminShiftDetail.js, edit mode)
 *
 * Renders the same image-card preset gallery, attire restriction
 * checkboxes, grooming requirement checkboxes, and send-home warning
 * switch in every editing surface so Create == Edit == Talent view.
 *
 * Props are uncontrolled-on-the-outside / controlled-from-the-outside:
 * pass the value + the change callback. Values follow the same shape
 * the backend persists to the `shifts` table.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import api from '../utils/api';

export default function DressCodeGroomingPicker({
  // Selected preset object: { id, name, description, image_url, checklist[] }
  //  - When the parent only has dress_code_name + description (no id),
  //    pass `selectedDressCodeName` instead and we'll match by name.
  value,
  onChange,
}) {
  const [presets, setPresets] = useState({
    dressCodes: [],
    attireRestrictions: [],
    groomingRequirements: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/presets/all');
        if (cancelled) return;
        setPresets({
          dressCodes: res.data?.dress_codes || [],
          attireRestrictions: res.data?.attire_restrictions || [],
          groomingRequirements: res.data?.grooming_requirements || [],
        });
      } catch {
        /* presets are non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve selected dress code preset from either {selectedDressCode}
  // or {dress_code_name} so this works for both Create (object) and
  // Edit (we only have the stored name + description on a saved row).
  const selectedDressCode =
    value?.selectedDressCode
    || presets.dressCodes.find((p) => p.name === value?.dress_code_name)
    || null;

  const attireRestrictions = Array.isArray(value?.attire_restrictions) ? value.attire_restrictions : [];
  const groomingRequirements = Array.isArray(value?.grooming_requirements) ? value.grooming_requirements : [];
  const sendHomeWarning = !!value?.send_home_warning;

  const emit = (patch) => onChange?.({ ...value, ...patch });

  const pickPreset = (dc) => {
    const isToggle = selectedDressCode?.id === dc.id;
    if (isToggle) {
      emit({
        selectedDressCode: null,
        dress_code_name: null,
        dress_code_description: '',
        dress_code_images: [],
        dress_code_checklist: [],
      });
    } else {
      emit({
        selectedDressCode: dc,
        dress_code_name: dc.name,
        dress_code_description: dc.description || '',
        dress_code_images: dc.image_url ? [dc.image_url] : (dc.images || []),
        dress_code_checklist: dc.checklist || [],
      });
    }
  };

  const toggleAttire = (label, checked) => {
    emit({
      attire_restrictions: checked
        ? [...attireRestrictions, label]
        : attireRestrictions.filter((x) => x !== label),
    });
  };

  const toggleGrooming = (label, checked) => {
    emit({
      grooming_requirements: checked
        ? [...groomingRequirements, label]
        : groomingRequirements.filter((x) => x !== label),
    });
  };

  return (
    <div className="space-y-6" data-testid="dress-code-grooming-picker">
      {/* Dress Code preset gallery */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <Label className="text-gray-900 font-semibold">Dress Code</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {presets.dressCodes.map((dc) => (
            <div
              key={dc.id}
              className={`relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                selectedDressCode?.id === dc.id
                  ? 'border-cyan-500 ring-2 ring-cyan-200'
                  : 'border-gray-200 hover:border-gray-400'
              }`}
              onClick={() => pickPreset(dc)}
              data-testid={`dress-code-${dc.id}`}
            >
              <img src={dc.image_url} alt={dc.name} className="w-full h-32 object-cover" />
              <div className="p-2 bg-white">
                <p className="text-xs font-semibold text-gray-900 truncate">{dc.name}</p>
              </div>
              {selectedDressCode?.id === dc.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
        {selectedDressCode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2" data-testid="dress-code-selected-summary">
            <p className="text-sm font-medium text-blue-900">{selectedDressCode.name}</p>
            {selectedDressCode.description && (
              <p className="text-xs text-blue-700 mt-1">{selectedDressCode.description}</p>
            )}
            <ul className="mt-2 space-y-1">
              {(selectedDressCode.checklist || []).map((item, i) => (
                <li key={i} className="flex items-center gap-1.5 text-xs text-blue-800">
                  <CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Attire Restrictions */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <Label className="text-gray-900 font-semibold">Attire Restrictions</Label>
        <div className="grid grid-cols-2 gap-2">
          {presets.attireRestrictions.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`attire-${r.id}`}>
              <input
                type="checkbox"
                checked={attireRestrictions.includes(r.label)}
                onChange={(e) => toggleAttire(r.label, e.target.checked)}
                className="rounded border-gray-300 text-cyan-500"
              />
              <span className="text-gray-700">{r.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Grooming Requirements */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <Label className="text-gray-900 font-semibold">Grooming Requirements</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {presets.groomingRequirements.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`grooming-${r.id}`}>
              <input
                type="checkbox"
                checked={groomingRequirements.includes(r.label)}
                onChange={(e) => toggleGrooming(r.label, e.target.checked)}
                className="rounded border-gray-300 text-cyan-500"
              />
              <span className="text-gray-700">{r.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Send Home Warning */}
      <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Switch
          checked={sendHomeWarning}
          onCheckedChange={(checked) => emit({ send_home_warning: !!checked })}
          data-testid="send-home-warning-switch"
        />
        <div>
          <p className="text-sm font-medium text-amber-900">Send Home Warning</p>
          <p className="text-xs text-amber-700">Display: &ldquo;This partner may send you home if these requirements are not met.&rdquo;</p>
        </div>
      </div>
    </div>
  );
}
